// PortalFetchWrapper is a wrapper component that fetches authorized funds and passes them to the wrapped component.
// It uses react-query for data fetching and handles loading and error states.

import { ReactElement } from 'react-markdown/lib/react-markdown';
import RaptorLoading from '../../../feedback/RaptorLoading';
import NoDataMessage from '../../../feedback/NoDataMessage.component';
import React from 'react';
import { usePositionPortalConfigs } from './services/queries';

interface PortalFetchWrapperProps {
  component: ReactElement;
}

export const PortalFetchWrapper = <T extends object>(
  props: PortalFetchWrapperProps & T,
) => {
  const { component } = props;

  const { data, isPending, error } = usePositionPortalConfigs();

  if (isPending) {
    return (
      <RaptorLoading
        centerWrap
        messages={[
          'Loading Potential Portal Funds',
          'This may take a few seconds...',
        ]}
      />
    );
  }

  if (error) {
    if (error.response?.status === 401) {
      return (
        <NoDataMessage
          message={
            'You are not authorised to access this module. Please contact support if you wish to gain portal access.'
          }
        />
      );
    }

    if (error.response?.status === 404) {
      if (error.response.data.detail === 'Module configuration not found.') {
        return (
          <NoDataMessage
            message={
              'Module configuration not found. Please contact support if you wish to gain access.'
            }
          />
        );
      } else {
        return (
          <NoDataMessage
            message={
              'Fund is currently processing. Position Data can be updated after the fund has finished processing. Please visit the "Report Delivery Oversight" page to confirm. If the fund is finished processing and this issue persists, please contact support.'
            }
          />
        );
      }
    }

    return <NoDataMessage message={error?.message} />;
  }

  if (!data?.authorised_funds) {
    return <NoDataMessage message={'No Authorised Funds Available'} />;
  }

  const authConfig = data;

  // Pass authFunds along with other props to the component
  return <>{React.cloneElement(component, { authConfig, ...props })}</>;
};
